import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Box,
  InputAdornment
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { FaArrowLeft, FaSearch, FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { cvManagementApi } from '../../../services/api';
import { Container, Header, HeaderLeft, BackButton, Title, Content, HeaderButton, ControlsContainer } from '../../common/styles';
import { MainContainer, LanguagesGrid, LanguageCard, LanguageCardContent, LanguageTitle, LanguageDetailsGrid, ActionButtons, ItalicText } from './styles';
import { COLORS } from '../../../constants/radar';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import { useAlert } from '../../../contexts/AlertContext';

const Languages = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState({ name: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showControls, setShowControls] = useState(true);

  useEffect(() => {
    fetchLanguages();
  }, []);

  const fetchLanguages = async () => {
    try {
      setLoading(true);
      const data = await cvManagementApi.getAllLanguages();
      setLanguages(data);
    } catch (err) {
      showAlert('Failed to load languages.', 'error', err);
      console.error('Error fetching languages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (language = null) => {
    if (language) {
      setCurrentLanguage(language);
      setIsEditing(true);
    } else {
      setCurrentLanguage({ name: '' });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDeleteDialog = (language) => {
    setCurrentLanguage(language);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentLanguage({
      ...currentLanguage,
      [name]: value
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (isEditing) {
        await cvManagementApi.updateLanguage(currentLanguage.id, currentLanguage);
        showAlert('Language updated successfully', 'success');
      } else {
        await cvManagementApi.createLanguage(currentLanguage);
        showAlert('Language created successfully', 'success');
      }
      handleCloseDialog();
      await fetchLanguages();
    } catch (err) {
      showAlert(err.message || `Failed to ${isEditing ? 'update' : 'create'} language. Please try again.`, 'error', err);
      console.error(`Error ${isEditing ? 'updating' : 'creating'} language:`, err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await cvManagementApi.deleteLanguage(currentLanguage.id);
      showAlert('Language deleted successfully', 'success');
      handleCloseDeleteDialog();
      await fetchLanguages();
    } catch (err) {
      showAlert(err.message || 'Failed to delete language. Please try again.', 'error', err);
      console.error('Error deleting language:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredLanguages = languages.filter(language => 
    language.name.toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  if (loading && languages.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/cv-configuration')}>
            <FaArrowLeft /> Back to CV Configuration
          </BackButton>
          <Title>Languages Configuration</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <ControlsContainer $show={showControls} style={{ marginBottom: '20px' }}>
          <Box sx={{ 
            display: 'flex', 
            gap: '16px', 
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flex: 1 }}>
              <TextField
                placeholder="Search Languages"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  maxWidth: '500px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    height: '40px',
                    fontSize: '14px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    }
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 14px',
                  }
                }}
              />
            </Box>

            <HeaderButton 
              onClick={() => handleOpenDialog()}
              sx={{
                minWidth: 'auto',
                height: '32px',
                padding: '0 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                alignSelf: 'flex-start',
                '&:hover': {
                  backgroundColor: '#1d4ed8',
                }
              }}
            >
              <FaPlus style={{ marginRight: '8px' }} /> Add Language
            </HeaderButton>
          </Box>
        </ControlsContainer>

        <MainContainer>
          <div style={{ 
            color: '#64748B', 
            fontSize: '14px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <span style={{ fontWeight: 600 }}>
              {filteredLanguages.length}
            </span>
            {filteredLanguages.length === 1 ? 'language' : 'languages'} found
          </div>

          {filteredLanguages.length === 0 ? (
            <EmptyState
              title="No languages found"
              description={searchTerm ? 
                "No languages match your search criteria. Try adjusting your search term." : 
                "No languages have been added yet. Click the '+ Add Language' button to add your first language."}
            />
          ) : (
            <LanguagesGrid>
              {filteredLanguages.map((language) => (
                <LanguageCard key={language.id}>
                  <LanguageCardContent>
                    <LanguageTitle>
                      {language.name}
                    </LanguageTitle>
                    <ActionButtons>
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenDialog(language)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenDeleteDialog(language)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ActionButtons>
                  </LanguageCardContent>
                </LanguageCard>
              ))}
            </LanguagesGrid>
          )}
        </MainContainer>
      </Content>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Language' : 'Add Language'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Language Name"
            type="text"
            fullWidth
            variant="outlined"
            value={currentLanguage.name}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={!currentLanguage.name.trim()}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the language "{currentLanguage.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Languages; 